import { Influencer } from '../model/InfluencerList.types';
import {
  FilterRule,
  FILTER_FIELD_CONFIGS,
  OPERATOR_LABELS,
} from './InfluencerList.filterTypes';

export type InfluencerSortField = 'name' | 'publishedAt' | 'createdAt';
export type InfluencerSortDirection = 'asc' | 'desc';

export type InfluencerSorting = {
  field: InfluencerSortField;
  direction: InfluencerSortDirection;
};

export type DateRangeFilter = 'all' | 'this-month' | 'last-month';

export const toFullName = (influencer: Influencer) =>
  `${influencer.firstName} ${influencer.lastName}`.trim();

export const toBusinessModelLabel = (businessModel: string | null): string => {
  if (!businessModel) return 'Legacy';
  return businessModel.toLowerCase() === 'bundle' ? 'Offer' : 'Legacy';
};

export const toDateRange = (
  filter: DateRangeFilter,
  now: Date = new Date(),
): { from: number; to: number } | null => {
  if (filter === 'all') return null;

  if (filter === 'this-month') {
    const from = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
    return { from, to: now.getTime() };
  }

  const from = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1);
  const to = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  return { from, to };
};

const compareNullableNumber = (
  left: number | null,
  right: number | null,
  direction: InfluencerSortDirection,
) => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (direction === 'asc') return left - right;
  return right - left;
};

export const sortInfluencers = (
  influencers: Influencer[],
  sorting: InfluencerSorting,
): Influencer[] => {
  const sorted = [...influencers];

  sorted.sort((left, right) => {
    if (sorting.field === 'name') {
      const leftName = toFullName(left).toLowerCase();
      const rightName = toFullName(right).toLowerCase();
      const result =
        sorting.direction === 'asc'
          ? leftName.localeCompare(rightName)
          : rightName.localeCompare(leftName);
      if (result !== 0) return result;
      return left.id - right.id;
    }

    if (sorting.field === 'createdAt') {
      return compareNullableNumber(
        left.createdAt,
        right.createdAt,
        sorting.direction,
      );
    }

    return compareNullableNumber(
      left.publishedAt,
      right.publishedAt,
      sorting.direction,
    );
  });

  return sorted;
};

export const toggleSort = (
  currentSort: InfluencerSorting,
  field: InfluencerSortField,
): InfluencerSorting => {
  if (currentSort.field !== field) {
    return {
      field,
      direction: field === 'name' ? 'asc' : 'desc',
    };
  }

  return {
    field,
    direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
  };
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

const getFieldLabel = (fieldKey: string): string =>
  FILTER_FIELD_CONFIGS.find((f) => f.key === fieldKey)?.label ?? fieldKey;

const getEnumLabel = (fieldKey: string, value: string | number): string => {
  const config = FILTER_FIELD_CONFIGS.find((f) => f.key === fieldKey);
  if (!config?.enumOptions) return String(value);
  return (
    config.enumOptions.find((o) => o.value === value)?.label ?? String(value)
  );
};

const formatFilterValue = (rule: FilterRule): string => {
  if (rule.value == null) return '';

  const config = FILTER_FIELD_CONFIGS.find((f) => f.key === rule.field);
  if (config?.fieldType === 'enum') return getEnumLabel(rule.field, rule.value);

  if (rule.operator === 'last-n-days') return `${rule.value} days`;

  if (
    (rule.operator === 'before' || rule.operator === 'after') &&
    typeof rule.value === 'number'
  ) {
    return dateFormatter.format(new Date(rule.value));
  }

  return String(rule.value);
};

export const toFilterDisplayText = (rule: FilterRule): string => {
  const fieldLabel = getFieldLabel(rule.field);
  const operatorLabel = OPERATOR_LABELS[rule.operator];
  const valueLabel = formatFilterValue(rule);

  return valueLabel
    ? `${fieldLabel} ${operatorLabel} ${valueLabel}`
    : `${fieldLabel} ${operatorLabel}`;
};

export const toDateLabel = (timestamp: number | null): string => {
  if (timestamp == null) return '–';
  return dateFormatter.format(new Date(timestamp));
};

export const FALLBACK_AVATAR_URL = `${
  import.meta.env.BASE_URL
}/static/images/user-icon@2x.png`.replace(/\/\//g, '/');

export const toAvatarUrl = (mediaCoverPath: string | null): string => {
  if (!mediaCoverPath) return FALLBACK_AVATAR_URL;

  return `${import.meta.env.VITE_IMAGE_HANDLER_URL}/40x40/${mediaCoverPath}`;
};
